/**
 * Componente de Rota de Admin
 * Verifica autenticação e papel de administrador antes de renderizar filhos.
 * Se não autenticado: redireciona para /login
 * Se autenticado mas não admin: redireciona para /dashboard
 * Se admin: renderiza filhos
 */
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { httpClient } from '@/shared/lib/httpClient';

interface UserInfo {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface AdminRouteProps {
  children: ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const { data: userInfo, isLoading: isUserLoading } = useQuery<UserInfo>({
    queryKey: ['user-info'],
    queryFn: async () => {
      return httpClient.get<UserInfo>('/auth/me');
    },
    enabled: isAuthenticated && !!user,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = isAuthLoading || isUserLoading;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex items-center space-x-2">
          <span className="h-8 w-8 animate-spin rounded-full border-4 border-visagio-mint border-t-transparent"></span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (userInfo?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
